import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import './AuthPages.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('El. paštas privalomas');
      return;
    }

    if (!validateEmail(email)) {
      setError('Neteisingas el. pašto formatas');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Klaida siunčiant el. laišką. Bandykite dar kartą.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          {/* Breadcrumbs */}
          <div className="breadcrumbs">
            <Link to="/" className="breadcrumb-link">Pradžia</Link>
            <ChevronRight size={14} className="separator" />
            <span className="current">Slaptažodžio atstatymas</span>
          </div>

          {/* Success Card */}
          <div className="auth-card">
            <div className="success-state">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h1>Laiškas išsiųstas!</h1>
              <p>
                Slaptažodžio atstatymo nuoroda išsiųsta į <strong>{email}</strong>
              </p>
              <p className="hint">
                Patikrinkite savo el. pašto dėžutę ir sekite nuorodą laiške. 
                Jei negavote laiško, patikrinkite šlamšto (spam) aplanką.
              </p>
              <div className="success-actions">
                <Link to="/login" className="btn-primary">
                  Grįžti į prisijungimą
                </Link>
                <button 
                  type="button" 
                  className="btn-link"
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                >
                  Siųsti dar kartą
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <Link to="/" className="breadcrumb-link">Pradžia</Link>
          <ChevronRight size={14} className="separator" />
          <Link to="/login" className="breadcrumb-link">Prisijungimas</Link>
          <ChevronRight size={14} className="separator" />
          <span className="current">Slaptažodžio atstatymas</span>
        </div>

        {/* Forgot Password Card */}
        <div className="auth-card">
          <div className="auth-header">
            <div className="header-icon">
              <Mail size={32} />
            </div>
            <h1>Pamiršote slaptažodį?</h1>
            <p>Įveskite savo el. pašto adresą ir mes atsiųsime nuorodą slaptažodžio atstatymui.</p>
          </div>

          {error && (
            <div className="error-alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">El. paštas</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                className={error ? 'error' : ''}
                placeholder="jusu@email.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Siunčiama...' : 'Siųsti atstatymo nuorodą'}
            </button>
          </form>

          <div className="auth-footer">
            <Link to="/login" className="back-link">
              <ArrowLeft size={16} />
              Grįžti į prisijungimą
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;

